import { useState, useEffect, useRef, useTransition, forwardRef, useImperativeHandle } from 'react';
import { Spinner, Checkbox, TreeView, TreeViewItem, Typography, SearchField } from "@volvo/vcdk-react";
import { CheckboxChangeEvent, TextFieldCustomEvent } from "@volvo/vcdk";
import { Product, Application } from '../../Types';
import { useGetProductsQuery } from '../../store/InvoiceApiSlice';
import { CustomTreeViewItem } from 'shared/CustomTreeViewItem';
import { truncate } from 'utils/common';

export const ProductCard = forwardRef((props, ref) => {
    
    const {data: productList = [], isLoading, isSuccess, isError, error} = useGetProductsQuery({});
    const [isPending, startTransition] = useTransition();
    const [isAllChecked, setIsAllChecked] = useState<boolean | undefined>(false);
    const [stateProductList, setStateProductList] = useState<Product[]>([]);
    const refProductList = useRef<Product[]>([]);
    const [valid, setValid] = useState(true);

    useImperativeHandle(ref, () => ({
        getSelectedProducts: () => {
            let selectedProducts = stateProductList.filter(product => product.checked);
            if(selectedProducts.length === 0) setValid(false);
            return {isAllChecked, selectedProducts};
        }
    }));
    
    useEffect(() => {
        if(isSuccess){
            startTransition(() => {
                setStateProductList(productList.map((product: Product) => ({
                    ...product,
                    checked: true,
                    applicationList: product.applicationList.map((app: Application) => ({ ...app, checked: false, visible: true })),
                    visible: true
                })));
                refProductList.current = productList;
                setIsAllChecked(true);
            });
        }
    }, [isSuccess, productList, startTransition]);

    const allChangeHandler = (e: CheckboxChangeEvent) => {
        let checked = e.detail.checked;
        if(checked) setValid(true);
        setStateProductList(stateProductList.map((product: Product) => ({
            ...product,
            applicationList: product.applicationList.map((application: Application) => ({...application, checked })),
            checked
        })));                
        setIsAllChecked(checked);
    }

    const productChangeHandler = (e: CheckboxChangeEvent, productID: number) => {
        let checked = e.detail.checked, allChecked = checked, allUnchecked = !checked;
        if(checked) setValid(true);
        setStateProductList(stateProductList.map((product: Product) => {
            if (product.productID === productID) product = {
                ...product,
                applicationList: product.applicationList.map((application: Application) => ({ ...application, checked })),
                checked
            }
            if(allChecked && !product.checked) allChecked = false;
            if(allUnchecked && product.checked) allUnchecked = false;
            return product;
        }));
        setIsAllChecked(allChecked ? true : (allUnchecked ? false : undefined));
    }

    const applicationChangeHandler = (e: CheckboxChangeEvent, application: Application) => {
        let checked = e.detail.checked, allProductChecked = checked, allProductUnchecked = !checked;
        if(checked) setValid(true);
        setStateProductList(stateProductList.map((product: Product) => {
            if(product.productID === application.productID) {
                let allAppChecked = checked, allAppUnchecked = !checked;
                product = {
                    ...product,
                    applicationList: product.applicationList.map((app: Application) => {
                        if(app.applicationID === application.applicationID) app = { ...app, checked }
                        if(allAppChecked && !app.checked) allAppChecked = false;
                        if(allAppUnchecked && app.checked) allAppUnchecked = false;                        
                        return app;
                    }),
                    checked: allAppChecked ? true : (allAppUnchecked ? false : undefined)
                }                
            }
            if(allProductChecked && !product.checked) allProductChecked = false;
            if(allProductUnchecked && product.checked !== false) allProductUnchecked = false;
            return product;
        }));
        setIsAllChecked(allProductChecked ? true : (allProductUnchecked ? false : undefined));
    }

    const searchHandler = (e: CustomEvent<TextFieldCustomEvent>) => {
        const searchValue = e.detail.value.toLowerCase();
        startTransition(() => {
            setStateProductList(prevState => prevState.map((product: Product) => {
                let productVisible = false;
                return {
                    ...product, 
                    applicationList: product.applicationList.map((app: Application) => {
                        let appVisible = app.name.toLowerCase().includes(searchValue);
                        if(appVisible && productVisible === false) productVisible = true;
                        return {...app, visible: appVisible};
                    }),
                    visible: product.name.toLowerCase().includes(searchValue) || productVisible
                }
            }));
        });
    }

    const clearSearchHandler = () => {
        startTransition(() => {
            setStateProductList(prevState => prevState.map((product: Product) => {
                return {
                    ...product, 
                    applicationList: product.applicationList.map((app: Application) => ({...app, visible: true})),
                    visible: true
                }
            }));
        });
    }

    const toggleHandler = (open: boolean, element: any) => {
        if(!element.dataset.product) return;
        const productId = parseInt(element.dataset.product, 10);
        setStateProductList(prevState => prevState.map((product: Product) => {
            if (product.productID === productId) product = {...product, opened: open};
            return product;
        }));
    }

    return (<div style={{ border: '1px solid #E1E1E1', borderRadius: 8, padding: 20, backgroundColor: '#F7F7F7', gap: 8, height: 629, display: 'grid' }}>
            <Typography variant='subtitle1' style={{color: valid ? 'inherit' : '#BF2012'}}>Product and application</Typography>
            <SearchField onInput={searchHandler} onClickButton={clearSearchHandler} size="small" placeholder="Search" icon='search' />
            <TreeView style={{ border: '1px solid #A9A9A9', height: 517, backgroundColor: '#FFFFFF', borderRadius: 4, padding: 8, overflowY: 'auto' }}>
                <TreeViewItem open={true} key={'all-products'}>
                    <Checkbox label={"All"} checked={isAllChecked} onChange={(e: CheckboxChangeEvent) => allChangeHandler(e)} />
                    {isLoading || isPending ? <Spinner size="small" /> : stateProductList.map((product: Product) => product.visible && (
                        <CustomTreeViewItem onToggle={toggleHandler} open={product.opened === true} data-product={product.productID} key={`product-${product.productID}`}>
                            <Checkbox checked={product.checked} onChange={(e: CheckboxChangeEvent) => productChangeHandler(e, product.productID)} label={truncate(product.name, 23)} title={product.name} />
                            {product.applicationList.map((application: Application) => application.visible && (
                                <TreeViewItem key={`application-${application.applicationID}`}>
                                    <Checkbox checked={application.checked} onChange={(e: CheckboxChangeEvent) => applicationChangeHandler(e, application)} label={truncate(application.name, 15)} title={application.name} />
                                </TreeViewItem>
                            ))}
                        </CustomTreeViewItem>
                    ))}
                </TreeViewItem>
            </TreeView>
        </div>)
})